import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import Loader from '../../ui/Loader/Loader';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PDFViewerProps = {
  fileUrl: string;
};

export function PDFViewer({ fileUrl }: PDFViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);
  const [hasRenderedFirstPage, setHasRenderedFirstPage] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(Math.floor(entry.contentRect.width));
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setNumPages(0);
    setIsDocumentLoaded(false);
    setHasRenderedFirstPage(false);
    setLoadError(null);
  }, [fileUrl]);

  const handleError = (error: Error) => {
    setIsDocumentLoaded(false);
    setHasRenderedFirstPage(false);
    setLoadError('Unable to load PDF.');
    console.error('PDF load error:', error);
  };

  const loadingContent = (
    <div className="flex min-h-[80vh] items-center justify-center">
      <Loader />
    </div>
  );

  const errorContent = (
    <div className="flex min-h-[80vh] items-center justify-center text-red-500">
      <p>{loadError}</p>
    </div>
  );

  const isLoading =
    !loadError &&
    (containerWidth === 0 || !isDocumentLoaded || !hasRenderedFirstPage);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full min-h-[70vh] overflow-auto"
    >
      {loadError ? errorContent : isLoading && loadingContent}

      <Document
        file={fileUrl}
        onLoadSuccess={(data: { numPages: number }) => {
          setNumPages(data.numPages);
          setIsDocumentLoaded(true);
          setLoadError(null);
        }}
        onLoadError={(error) => handleError(error)}
        onSourceError={(error) => handleError(error)}
        error={null}
        loading={null}
        onLoadProgress={undefined}
      >
        <div className={isLoading || loadError ? 'invisible' : ''}>
          {containerWidth > 0 &&
            Array.from({ length: numPages }, (_, i) => (
              <Page
                key={`${fileUrl}-${i + 1}`}
                pageNumber={i + 1}
                width={containerWidth}
                loading={null}
                onRenderSuccess={
                  i === 0 ? () => setHasRenderedFirstPage(true) : undefined
                }
              />
            ))}
        </div>
      </Document>
    </div>
  );
}
